// Catalog.ID Client — Ceremony Enrollment Records
// ============================================================================
// The side-effecting half of the ceremony modes, kept out of
// `ceremony-handlers.ts` so the gate logic there stays unit-testable with no
// network and no modals:
//
//   list/saveEnrollmentRecord — the off-ledger "which stream am I enrolled in"
//                               record, stored as a private CID claim
//
// Both are wired in by `client.ts`; the handlers treat either being absent as
// a named refusal rather than a crash.
//
// `persistAuthorityKey` used to live here too — mint a random hybrid seed,
// prompt for the supersecret, POST `registry_authority@n`. It is GONE
// (cid-encrypted-storage §5.2, M5): the authority key is derived from the
// account's derive root and only its generation state is stored, as the
// `cid/registry-authority` blob. There is no bag write left to gate, so there
// is no prompt left to show.
// ============================================================================

import * as accountApi from '../api/account';
import * as claimsApi from '../api/claims';
import { encryptClaim, decryptClaim } from '../crypto/claims';
import { keys } from '../state/keys';
import * as aes from '../crypto/aes';
import type { CeremonyEnrolledStreamWire } from './ceremony-types';

/**
 * The CID claim type that carries an enrollment record. Private by
 * construction — registered with an empty `PublishableBy` set server-side, so
 * it can never be published to operators. It holds no secret (a stream genesis
 * id and a public fingerprint are both public on the registry), but it maps a
 * Catalog.ID account to a Registry account stream, and that mapping is exactly
 * the anonymity boundary CID's D15 keeps off the ledger.
 */
export const ENROLLMENT_CLAIM_TYPE = 'registry_account_stream';

/**
 * Every enrollment record on this account, newest write per stream.
 *
 * Failures are the caller's to absorb: the handler answers with an empty list
 * rather than turning a successful key disclosure into an error, because CPR's
 * own vault record — not this one — is what its local logic reads.
 */
export async function listEnrollmentRecords(): Promise<CeremonyEnrolledStreamWire[]> {
    const attrSecretB64 = keys.getPrivateKey('attr_encrypt');
    if (!attrSecretB64) return [];

    const summaries = await claimsApi.listClaims();
    const mine = summaries.filter(c => c.claimType === ENROLLMENT_CLAIM_TYPE);

    // Newest record per stream wins (see saveEnrollmentRecord: a superseded
    // write can briefly leave two).
    const newest = new Map<string, CeremonyEnrolledStreamWire>();
    for (const summary of mine) {
        try {
            const detail = await claimsApi.getClaim(summary.claimUuid);
            const selfKey = detail.keys.find(k => k.recipientType === 'self');
            if (!selfKey) continue;
            const json = await decryptClaim(
                detail.encryptedValue, detail.nonce, selfKey.encryptedKey,
                aes.fromBase64(attrSecretB64),
            );
            const parsed = JSON.parse(json) as Partial<CeremonyEnrolledStreamWire>;
            if (!parsed.streamGenesisId || !parsed.operatorId
                || !parsed.handle || !parsed.authorityFingerprint) continue;
            const record: CeremonyEnrolledStreamWire = {
                streamGenesisId: parsed.streamGenesisId,
                operatorId: parsed.operatorId,
                handle: parsed.handle,
                authorityFingerprint: parsed.authorityFingerprint,
                updatedAt: parsed.updatedAt ?? '',
            };
            const prior = newest.get(record.streamGenesisId);
            if (!prior || prior.updatedAt <= record.updatedAt) {
                newest.set(record.streamGenesisId, record);
            }
        } catch {
            // One unreadable record (a rotated attr_encrypt key with no chain
            // walk here yet) must not hide the others.
            continue;
        }
    }
    return [...newest.values()];
}

/**
 * Record — or update — the enrollment for one (stream, operator).
 *
 * Update-in-place rather than append: after a rotation the authority
 * fingerprint changes but the stream is the SAME stream (its identity is the
 * genesis claim id, which never changes, D3). Appending would leave a second
 * record naming the retired key, and a fresh machine reading the list would
 * have no way to tell which one is current.
 */
export async function saveEnrollmentRecord(record: CeremonyEnrolledStreamWire): Promise<string> {
    const pubKeys = await accountApi.getOwnPublicKeys();
    const attrPub = pubKeys.find(k => k.keyId === 'attr_encrypt');
    if (!attrPub) throw new Error('no-attr-encrypt-key');

    const value = JSON.stringify(record);
    const label = `Registry stream ${record.handle.slice(0, 8)}… @ ${record.operatorId}`;
    const encrypted = await encryptClaim(value, aes.fromBase64(attrPub.publicKey), label);

    // Supersede rather than update in place. `PUT /api/claims/{uuid}` rewrites
    // only `{encryptedValue, nonce}` and keeps the ORIGINAL wrapped symmetric
    // key, so an update would have to re-encrypt under a key this module would
    // first have to unwrap — a decrypt-modify-reencrypt round trip whose only
    // gain is a stable uuid nobody references. Create-then-delete keeps one
    // record per stream with no partial-write window: a failure after the
    // create leaves two readable records, and the reader picks the newest.
    const created = await claimsApi.createClaim({
        claimType: ENROLLMENT_CLAIM_TYPE,
        encryptedValue: encrypted.encryptedValue,
        nonce: encrypted.nonce,
        encryptedKey: encrypted.encryptedKey,
        ...(encrypted.encryptedLabel ? { encryptedLabel: encrypted.encryptedLabel } : {}),
        ...(encrypted.labelNonce ? { labelNonce: encrypted.labelNonce } : {}),
    });

    for (const stale of await findRecordUuids(record.streamGenesisId, created.claimUuid)) {
        try { await claimsApi.deleteClaim(stale); }
        catch { /* best-effort tidy; the newest record still wins on read */ }
    }
    return created.claimUuid;
}

/** Claim uuids holding a record for this stream, excluding `exceptUuid`. */
async function findRecordUuids(streamGenesisId: string, exceptUuid: string): Promise<string[]> {
    const attrSecretB64 = keys.getPrivateKey('attr_encrypt');
    if (!attrSecretB64) return [];
    const summaries = await claimsApi.listClaims();
    const out: string[] = [];
    for (const summary of summaries.filter(c => c.claimType === ENROLLMENT_CLAIM_TYPE)) {
        if (summary.claimUuid === exceptUuid) continue;
        try {
            const detail = await claimsApi.getClaim(summary.claimUuid);
            const selfKey = detail.keys.find(k => k.recipientType === 'self');
            if (!selfKey) continue;
            const json = await decryptClaim(
                detail.encryptedValue, detail.nonce, selfKey.encryptedKey,
                aes.fromBase64(attrSecretB64),
            );
            const parsed = JSON.parse(json) as { streamGenesisId?: string };
            if (parsed.streamGenesisId === streamGenesisId) out.push(summary.claimUuid);
        } catch {
            continue;
        }
    }
    return out;
}
